/* eslint-disable import/prefer-default-export */

import styled, { css } from 'styled-components';

// ASSETS
import CalendarIcon from 'assets/CalendarIcon.svg';

// COMPONENTS
import { ErrorMessage } from 'formik';
import { Select } from 'components/Select';

// STYLES
import colors, { opacity, blend } from 'styles/colors';
import { INPUT_BORDER, INPUT_BORDER_RADIUS, INPUT_BORDER_WIDTH, INPUT_STYLES_MIXIN } from 'styles/form';
import 'react-datepicker/dist/react-datepicker.css';
import { IconButton, RemoveButton } from 'components/Buttons';
import { CURRENCY_DEFAULT_SIZE } from 'assets/Currencies/constants';
import { fontWeight } from 'styles/common';

export const FieldContainer = styled.div`
  display: flex;
`;

const ErrorMessageStyle = css`
  color: ${colors.red};
  font-size: 12px;
`;

export const StyledErrorMessage = styled(ErrorMessage)`
  ${ErrorMessageStyle}
`; // Defined as `Styled...` so it can selected directly by `styled-components`

export const FormFieldContainer = styled.div<{ errored?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 4px;

  & label {
    font-size: 12px;
  }
`;

export const Input = styled.input<{ errored?: boolean; dark?: boolean }>`
  &&&& {
    ${INPUT_STYLES_MIXIN}
    padding: 5px 8px;
  }
  cursor: text;

  &&&&:disabled {
    opacity: 1;
    background-color: ${colors.lightBackground};
    color: ${colors.primary};

    &:hover {
      border: ${INPUT_BORDER};
    }
  }

  &&&&:disabled:read-only {
    cursor: auto;
  }

  &&&&:disabled:read-only:placeholder-shown {
    ::placeholder {
      opacity: 0;
    }
  }

  &&&&& {
    ${({ errored }) => (errored ? `border-color: ${colors.red}` : '')};
  }

  ${({ dark, errored }) =>
    dark &&
    `&&&& {
      background-color: ${opacity(colors.fleetOffGray, 0.08)};
      color: ${colors.white};
      border: 1px solid ${opacity(colors.fleetOffGray, 0.1)};
      width: 100%;
      font-size: 16px;
    }

    &&&&:focus {
      border-color: ${opacity(colors.fleetOffGray, 0.2)};
    }

    &&&&&:hover {
      border-color: ${opacity(colors.fleetOffGray, 0.2)};
    }

    &&&&&& {
      ${errored ? `border-color: ${colors.red}` : ''};
    }

  `}
`;

export const PriceInput = styled(Input)`
  &&&& {
    padding-left: ${CURRENCY_DEFAULT_SIZE + 8}px;
  }

  &&&&:disabled {
    background-color: ${opacity(colors.fleetOffGray, 0.4)};
  }
`;

export const RateInput = styled(Input)`
  &&&& {
    padding-left: ${CURRENCY_DEFAULT_SIZE + 8}px;
    max-width: inherit;
  }

  &&&&:disabled {
    background-color: ${opacity(colors.fleetOffGray, 0.4)};
  }
`;

const SLASH_OFFSET = 12;

const SlashUnitBase = css`
  position: absolute;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  right: ${INPUT_BORDER_WIDTH}px;
  top: ${INPUT_BORDER_WIDTH}px;
  bottom: ${INPUT_BORDER_WIDTH}px;
  padding-left: ${SLASH_OFFSET + SLASH_OFFSET / 2}px;
  padding-right: ${SLASH_OFFSET}px;
  background-color: ${colors.fleetOffGray};
  color: ${colors.fleetSlateGray};
  clip-path: polygon(${SLASH_OFFSET}px 0, 100% 0, 100% 100%, 0% 100%);
  min-width: 75px;
`;

export const AgreementPriceInputWrapper = styled.div<{ unit?: string }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  max-width: 165px;

  input {
    padding: 10px;
    padding-right: 80px;
    width: calc(100% - 80px);
    box-sizing: border-box;
  }

  /* Top layer handles the 'unit' text and background of the div */
  ::after {
    ${SlashUnitBase}
    content: '${({ unit }) => unit || 'Item'}';
    z-index: 2;
    background-color: ${blend(
      // "blend" is used instead of "opacity" because the :before is behind with a color of #DADEE6
      colors.fleetOffGray,
      0.4
    )};
    border-top-right-radius: calc(${INPUT_BORDER_RADIUS} - 1px);
    border-bottom-right-radius: calc(${INPUT_BORDER_RADIUS} - 1px);
  }

  /* Bottom layer acting as the left side border */
  ::before {
    ${SlashUnitBase}
    content: '${({ unit }) => unit || 'Item'}';
    z-index: 1;
    right: ${INPUT_BORDER_WIDTH + 1}px;
    background-color: ${colors.fleetOffGray};
  }
`;

export const QuantityInput = styled(Input)`
  &&&&& {
    text-align: center;
    border-radius: 0;
    margin-top: -1px; // -1px so the input border overlaps the container border
    margin-bottom: -1px;
    padding: 5px 0;
  }
`;

export const QuantityInputContainer = styled.div`
  display: flex;
  border: 1px solid ${colors.fleetOffGray};
  border-radius: 4px;
  align-items: center;
`;

export const UpdateQuantityButton = styled(IconButton)`
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${colors.fleetBlue};
  margin: 5px;
`;

export const StyledTextArea = styled.textarea<{ errored?: boolean }>`
  ${INPUT_STYLES_MIXIN}
  padding: 7.5px 8px;
  height: inherit;
  cursor: text;
  resize: none;

  &:disabled:read-only {
    opacity: 1;
    cursor: auto;
    color: ${colors.black};
    background-color: ${colors.lightBackground};
    border: 1px solid ${colors.fleetOffGray};
  }

  &:disabled:read-only:placeholder-shown {
    ::placeholder {
      opacity: 0;
    }
  }

  ${({ errored }) => (errored ? `border-color: ${colors.red};` : '')}
`;

export const StyledSelect = styled(Select)<{ errored: string; dark?: boolean }>`
  .Select__control--is-disabled {
    background-color: #f0f2f5;

    .Select__placeholder {
      opacity: 0;
    }
  }

  .Select__option {
    cursor: pointer;
  }

  .Select__control {
    cursor: pointer;
    border-color: ${(props) => {
      if (props.errored) {
        return colors.red;
      }
      if (props.dark && !props.isActive) {
        return opacity(colors.fleetOffGray, 0.1);
      }

      if (props.dark && props.menuIsOpen) {
        return opacity(colors.fleetOffGray, 0.2);
      }

      return colors.fleetOffGray;
    }};
    ${({ dark }) => dark && `background-color: ${opacity(colors.fleetOffGray, 0.08)};`}
  }

  .Select__control:hover {
    ${({ dark }) => dark && `border-color: ${opacity(colors.fleetOffGray, 0.2)};`}
  }

  .Select__control--is-focused {
    ${({ dark }) => dark && `border-color: ${opacity(colors.fleetOffGray, 0.2)};`}
  }

  .Select__single-value {
    ${({ dark }) => dark && `color: ${colors.white};`}
  }

  .Select__menu {
    ${({ dark }) => dark && `background-color: #161926;`}
  }

  .Select__option--is-focused {
    ${({ dark }) => dark && `background-color: ${opacity(colors.fleetOffGray, 0.2)};`}
  }
`;

export const StyledDatePicker = styled.div<{ errored?: boolean; readOnly: boolean }>`
  &&&&& input {
    height: 30px;
    background: ${({ errored }) => (errored ? colors.inputErrorBackground : colors.white)} url(${CalendarIcon}) 2% 40%
      no-repeat;
    padding: 2px 8px;
    padding-left: 33px;
    border: 1px solid ${colors.fleetOffGray};
    width: 100%;
    color: ${({ errored }) => (errored ? colors.inputError : '')};

    font-size: 14px;
    font-weight: 600;
    opacity: 1;
    border-radius: 3px;

    ::placeholder {
      color: ${colors.black};
      opacity: 1;
    }

    :focus {
      box-shadow: ${({ readOnly }) => (readOnly ? 'none' : '')};
      border-color: ${colors.fleetBlue};
      background: ${({ errored }) => (errored ? colors.inputErrorBackground : colors.white)} url(${CalendarIcon}) 2% 40%
        no-repeat;
    }

    :disabled:read-only {
      background: ${colors.lightBackground} url(${CalendarIcon}) 2% 40% no-repeat;
      color: ${colors.black};
      cursor: ${({ readOnly }) => (readOnly ? 'default' : '')};
    }

    &.react-datepicker-time__input {
      background-image: none;
      padding-left: 8px;
    }
  }

  .react-datepicker {
    font-size: 12px !important;
    font-weight: 600 !important;
    border: 1px solid ${colors.fleetOffGray} !important;
    box-shadow: 0 2px 6px 0 ${colors.fleetOffGray};

    @media screen and (max-width: 1280px) {
      transform: scale(1.3) !important;
      margin-top: 36px;
      margin-bottom: 42px;
    }
  }

  .react-datepicker-wrapper {
    width: 100% !important;
  }

  .react-datepicker__triangle {
    border-bottom-color: white !important;

    :before {
      border-bottom-color: ${colors.fleetOffGray} !important;
    }

    :after {
      border-bottom-color: white !important;
    }
  }

  .react-datepicker__header {
    background-color: inherit;
    border-bottom: 1px solid ${colors.fleetOffGray} !important;
  }

  .react-datepicker__current-month {
    min-width: 99px !important;
  }

  .react-datepicker__header--time {
    display: none;
  }

  .react-datepicker__day-names {
    width: 207px;
  }

  .react-datepicker__time-container {
    width: 86px !important;
    border-left: 1px solid ${colors.fleetOffGray} !important;
  }

  .react-datepicker__time-list {
    height: 234px !important;

    /* Hide scrollbar for Chrome, Safari and Opera */
    ::-webkit-scrollbar {
      display: none;
    }

    /* Hide scrollbar for IE, Edge and Firefox */
    -ms-overflow-style: none;
    scrollbar-width: none;
  }

  .react-datepicker__time-list-item {
    height: 30px !important;
    border-radius: 3px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 !important;
    color: ${colors.fleetBlue} !important;

    :hover {
      background-color: ${colors.fleetOffGray}60 !important;
    }
  }

  .react-datepicker__day--selected {
    color: white !important;
    background-color: ${colors.fleetBlue} !important;
  }

  .react-datepicker__time-list-item--selected {
    radius: 3px !important;
    font-weight: inherit !important;
    background-color: ${colors.fleetOffGray}40 !important;
  }

  .react-datepicker__day--today {
    font-weight: inherit;
  }

  .react-datepicker__day--in-range {
    color: black;
    background-color: ${colors.fleetOffGray}60;
  }

  .react-datepicker__day--in-selecting-range {
    color: black;
    background-color: ${colors.fleetOffGray}60;
  }
`;

export const CustomHeaderContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  justify-content: space-between;
`;

export const StyledArrowButton = styled.button<{ disabled: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 30px;
  height: 30px;
  padding: 0;
  margin: 0 12px;
  border: none;
  opacity: ${({ disabled }) => (disabled ? 0.4 : 1)};
  :hover {
    border-radius: 3px;
    background-color: ${({ disabled }) => (disabled ? 'inherit' : `${colors.fleetOffGray}60`)};
  }
`;

export const ToggleFieldContainer = styled.div`
  display: flex;
  justify-content: space-between;
`;

export const ToggleFieldMessageContainer = styled.div`
  display: flex;
  justify-content: flex-start;
`;

export const ToggleFieldMessage = styled.span`
  display: flex;
  align-items: center;
  font-weight: 600;
`;

export const ToggleContainer = styled.div`
  display: flex;
  justify-content: flex-end;
`;

export const Toggle = styled.input`
  appearance: none;
  /* For iOS < 15 to remove gradient background */
  background-color: ${colors.white};
  /* Not removed via appearance */
  margin: 0;

  background-color: ${opacity(colors.fleetOffGray, 0.4)};
  width: 4.1em;
  height: 2em;
  border: 1px solid ${colors.fleetOffGray};
  border-radius: 1em;
  cursor: pointer;

  display: flex;
  box-sizing: border-box;
  position: relative;

  transition: border-color ease 280ms, background-color ease 280ms;

  &:focus {
    box-shadow: 1px 1px 7px ${opacity(colors.black, 0.2)};
  }

  &::before {
    content: '';
    box-shadow: 1px 1px 7px ${opacity(colors.black, 0.2)};
    height: 2em;
    width: 2em;
    position: absolute;
    top: -1px;
    left: -1px;
    box-sizing: border-box;
    border: 1px solid ${colors.fleetOffGray};
    border-radius: 1em;
    background-color: ${colors.white};
    transition: left ease 280ms;
  }

  &:checked {
    border-color: #1d8e07; // This is colors.green with a 20% black added.
    background-color: ${colors.green};

    &::before {
      left: 2.1em;
    }
  }
`;

export const StyledIcon = styled.div`
  display: flex;
  align-items: center;
  margin-left: 5px;
`;

export const MultiCheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 8px;
`;

export const MultiCheckboxLabel = styled.label`
  display: flex;
  align-items: center;
`;

export const MultiCheckboxError = styled.div`
  color: ${colors.red};
  margin-top: '0.25rem';
`;

export const HiddenCheckboxInput = styled.input`
  display: none;
`;

export const DateInputFieldContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

export const ErrorText = styled.div`
  ${ErrorMessageStyle}
`;

const photoRatio = '4 / 3'; // most used in phones

export const ImageSelector = styled.button<{ errored?: boolean }>`
  max-height: 20vh;
  width: 100%;
  gap: 4px;
  text-transform: uppercase;
  color: ${colors.fleetDarkGray};
  aspect-ratio: ${photoRatio};
  background-color: ${opacity(colors.fleetOffGray, 0.08)};
  border: 1px solid ${opacity(colors.fleetOffGray, 0.1)};
  border-radius: 4px;
  display: flex;
  font-weight: ${fontWeight.semibold};
  align-items: center;
  justify-content: center;
  cursor: pointer;

  ${({ errored }) =>
    errored &&
    css`
      border-color: ${colors.red};
      background-color: ${opacity(colors.red, 0.1)};
    `}
`;

export const ImagePreview = styled.img`
  max-height: 20vh;
  display: flex;
  width: 100%;
  aspect-ratio: ${photoRatio};
  object-fit: contain;
  background-color: ${opacity(colors.fleetOffGray, 0.08)};
  border: 1px solid ${opacity(colors.fleetOffGray, 0.1)};
  border-radius: 4px;
  height: auto;
`;

export const ImageSelectorContainer = styled.div`
  position: relative;
`;

export const ImageRemoveButton = styled(RemoveButton)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);

  &&:hover {
    background-color: ${blend(colors.fleetBlack, 0.08, colors.white)};
    mix-blend-mode: unset;
    color: ${colors.red};
  }
`;
